import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { blogAPI, contentAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  // Page selector state
  const [activeView, setActiveView] = useState('list'); // 'list' or 'create'

  // Form Data for editing (still used for edit functionality)
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    author_name: '',
    category: '',
    tags: [],
    cover_image_url: '',
    is_published: false,
  });

  // AI Blog Creation Form Data
  const [aiFormData, setAiFormData] = useState({
    blogType: 'how-to',
    topic: '',
    targetAudience: '',
    mainPoints: '',
    keywords: '',
    wordCount: '1000-1500',
    tone: 'informative',
    writingStyle: 'conversational',
    category: '',
    includeIntro: true,
    includeConclusion: true,
    includeFAQ: false,
    includeCTA: true,
    ctaType: 'read-more',
    seoOptimized: true,
    contentMode: 'text-only', // 'text-only' or 'text-with-images'
    language: 'en'
  });

  // Generated content
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');

  // Blog Types - matching frontend implementation
  const blogTypes = [
    { id: 'how-to', name: 'How-to Guide', icon: '📝', description: 'Step-by-step instructional content' },
    { id: 'listicle', name: 'Listicle', icon: '📋', description: 'Number-based list format (e.g., "10 Ways to...")' },
    { id: 'case-study', name: 'Case Study', icon: '📊', description: 'In-depth analysis with real examples' },
    { id: 'opinion', name: 'Opinion/Editorial', icon: '💡', description: 'Personal perspective or commentary' },
    { id: 'tutorial', name: 'Tutorial', icon: '🎓', description: 'Educational step-by-step guide' },
    { id: 'review', name: 'Product Review', icon: '🔍', description: 'Detailed product/service evaluation' },
    { id: 'news', name: 'News/Announcement', icon: '📰', description: 'Industry news or company updates' },
    { id: 'interview', name: 'Interview', icon: '🎙️', description: 'Q&A format with experts' },
    { id: 'comparison', name: 'Comparison', icon: '⚖️', description: 'Side-by-side comparison of options' },
    { id: 'roundup', name: 'Roundup', icon: '🔄', description: 'Collection of resources or tools' },
    { id: 'ultimate-guide', name: 'Ultimate Guide', icon: '📚', description: 'Comprehensive deep-dive content' },
    { id: 'thought-leadership', name: 'Thought Leadership', icon: '🌟', description: 'Industry insights and predictions' }
  ];

  // Content mode options
  const contentModeOptions = [
    { value: 'text-only', label: 'Text Only', description: 'Generate blog with text content only', icon: '📄' },
    { value: 'text-with-images', label: 'Text & Images', description: 'Generate blog with AI-generated images', icon: '🎨' }
  ];

  // Tone options
  const toneOptions = [
    { value: 'informative', label: 'Informative', description: 'Educational and factual' },
    { value: 'conversational', label: 'Conversational', description: 'Friendly and approachable' },
    { value: 'persuasive', label: 'Persuasive', description: 'Convincing and influential' },
    { value: 'professional', label: 'Professional', description: 'Formal and business-like' },
    { value: 'humorous', label: 'Humorous', description: 'Lighthearted and funny' },
    { value: 'technical', label: 'Technical', description: 'Detailed and specialized' },
    { value: 'inspirational', label: 'Inspirational', description: 'Motivational and uplifting' }
  ];

  // Writing style options
  const writingStyleOptions = [
    { value: 'conversational', label: 'Conversational' },
    { value: 'academic', label: 'Academic' },
    { value: 'journalistic', label: 'Journalistic' },
    { value: 'storytelling', label: 'Storytelling' },
    { value: 'direct', label: 'Direct' }
  ];

  // Word count options
  const wordCountOptions = [
    { value: '500-1000', label: '500-1000 words' },
    { value: '1000-1500', label: '1000-1500 words' },
    { value: '1500-2000', label: '1500-2000 words' },
    { value: '2000+', label: '2000+ words' }
  ];

  // Language options
  const languageOptions = [
    { value: 'en', label: 'English', flag: '🇬🇧' },
    { value: 'zh', label: '中文 (Chinese)', flag: '🇨🇳' },
    { value: 'es', label: 'Español (Spanish)', flag: '🇪🇸' },
    { value: 'de', label: 'Deutsch (German)', flag: '🇩🇪' },
    { value: 'fr', label: 'Français (French)', flag: '🇫🇷' },
    { value: 'ja', label: '日本語 (Japanese)', flag: '🇯🇵' },
    { value: 'ko', label: '한국어 (Korean)', flag: '🇰🇷' },
    { value: 'pt', label: 'Português (Portuguese)', flag: '🇵🇹' }
  ];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await blogAPI.getAllPosts();
      setPosts(response.data.posts || []);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  // AI Form Handlers
  const handleAiInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAiFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // AI Blog Generation
  const handleGenerateBlog = async () => {
    // Validation
    if (!aiFormData.topic.trim()) {
      setError('Please enter a blog topic');
      return;
    }

    if (!aiFormData.targetAudience.trim()) {
      setError('Please specify your target audience');
      return;
    }

    setError('');
    setIsGenerating(true);
    setGeneratedContent(null);

    // Update generation status based on content mode
    const isImageMode = aiFormData.contentMode === 'text-with-images';
    setGenerationStatus(
      isImageMode
        ? '📝 Step 1/3: Generating blog text...'
        : '📝 Generating blog content...'
    );

    try {
      // Simulate progress updates
      const statusInterval = setInterval(() => {
        setGenerationStatus((prevStatus) => {
          if (isImageMode) {
            if (prevStatus.includes('Step 1/3')) {
              return '🔍 Step 2/3: Analyzing content for image placements...';
            } else if (prevStatus.includes('Step 2/3')) {
              return '🎨 Step 3/3: Generating images...';
            }
          }
          return prevStatus;
        });
      }, isImageMode ? 8000 : 5000);

      const response = await contentAPI.generateBlogPost(aiFormData);

      clearInterval(statusInterval);
      setGenerationStatus(
        isImageMode
          ? '✅ Blog with images generation complete!'
          : '✅ Blog generation complete!'
      );

      // Display generated content after a short delay for better UX
      setTimeout(() => {
        setGeneratedContent(response.data.data);
        setGenerationStatus('');
      }, 1000);

    } catch (err) {
      console.error('Blog generation error:', err);
      setError(err.response?.data?.message || 'Failed to generate blog post. Please try again.');
      setGenerationStatus('');
    } finally {
      setIsGenerating(false);
    }
  };

  // Save generated content as draft
  const handleSaveAsDraft = async () => {
    if (!generatedContent) return;

    try {
      setLoading(true);
      setError('');

      const draftData = {
        title: generatedContent.title,
        excerpt: generatedContent.metaDescription || '',
        content: generatedContent.fullContent,
        author_name: 'Admin',
        category: aiFormData.category || '',
        tags: [],
        cover_image_url: generatedContent.coverImage || '',
        is_published: false,
      };

      const response = await blogAPI.createPost(draftData);

      // Backend returns { message: '...', post: {...} } without success field
      if (response.data && response.data.message) {
        // Refresh posts list
        fetchPosts();
        // Switch back to list view
        setActiveView('list');
        // Clear generated content
        setGeneratedContent(null);
      } else {
        setError('Failed to save draft');
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      setError('Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  // Clear generated content and form
  const handleClear = () => {
    setGeneratedContent(null);
    setAiFormData({
      blogType: 'how-to',
      topic: '',
      targetAudience: '',
      mainPoints: '',
      keywords: '',
      wordCount: '1000-1500',
      tone: 'informative',
      writingStyle: 'conversational',
      category: '',
      includeIntro: true,
      includeConclusion: true,
      includeFAQ: false,
      includeCTA: true,
      ctaType: 'read-more',
      seoOptimized: true,
      contentMode: 'text-only',
      language: 'en'
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData((prev) => ({
      ...prev,
      tags,
    }));
  };

  // No longer needed - using AI generation instead
  // const handleCreatePost = async () => { ... }

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      author_name: post.author_name || '',
      category: post.category || '',
      tags: post.tags || [],
      cover_image_url: post.cover_image_url || '',
      is_published: post.is_published || false,
    });
    setShowEditModal(true);
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;

    try {
      setLoading(true);
      setError('');

      const postData = {
        ...formData,
        tags: formData.tags.length > 0 ? formData.tags : [],
      };

      const response = await blogAPI.updatePost(editingPost.id, postData);
      // Backend returns { message: '...', post: {...} } without success field
      if (response.data && response.data.message) {
        setShowEditModal(false);
        setEditingPost(null);
        setFormData({
          title: '',
          excerpt: '',
          content: '',
          author_name: '',
          category: '',
          tags: [],
          cover_image_url: '',
          is_published: false,
        });
        fetchPosts();
      } else {
        setError('Failed to update post');
      }
    } catch (err) {
      console.error('Failed to update post:', err);
      setError('Failed to update post');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await blogAPI.deletePost(id);
      if (response.data.success) {
        fetchPosts();
      } else {
        setError('Failed to delete post');
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
      setError('Failed to delete post');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (id, publish) => {
    try {
      setLoading(true);
      setError('');

      const response = publish
        ? await blogAPI.publishPost(id)
        : await blogAPI.unpublishPost(id);

      // Backend returns { message: '...', post: {...} } without success field
      if (response.data && response.data.message) {
        fetchPosts();
      } else {
        setError(`Failed to ${publish ? 'publish' : 'unpublish'} post`);
      }
    } catch (err) {
      console.error('Failed to toggle publish:', err);
      setError(`Failed to ${publish ? 'publish' : 'unpublish'} post`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Blog Management" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading posts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Blog Management" />
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Page Selector */}
          <div className="mb-6">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveView('list')}
                className={`py-3 px-5 border-b-2 font-medium text-sm transition-all ${
                  activeView === 'list'
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Blogs List
              </button>
              <button
                onClick={() => setActiveView('create')}
                className={`py-3 px-5 border-b-2 font-medium text-sm transition-all ${
                  activeView === 'create'
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                Create
              </button>
            </div>
          </div>

          {/* Content Based on Active View */}
          {activeView === 'list' && (
            <>
              {/* Action Buttons */}
              <div className="mb-6 flex justify-between items-center">
                <p className="text-gray-600 text-sm">
                  Manage blog posts and comments. Click on a post to edit, or use the buttons below.
                </p>
              </div>

          {/* Posts Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Published At
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No blog posts found
                      </td>
                    </tr>
                  ) : (
                    posts.map((post) => (
                      <tr key={post.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{post.title}</div>
                          <div className="text-xs text-gray-500">{post.slug}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{post.author_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-block bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs">
                            {post.category || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {post.is_published ? (
                            <span className="inline-block bg-green-100 text-green-600 px-2 py-1 rounded text-xs">
                              Published
                            </span>
                          ) : (
                            <span className="inline-block bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                              Draft
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {post.published_at
                            ? new Date(post.published_at).toLocaleString()
                            : 'Not published'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleTogglePublish(post.id, !post.is_published)}
                            className={`mx-1 px-3 py-1 rounded text-xs font-medium transition ${
                              post.is_published
                                ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                                : 'bg-green-100 text-green-600 hover:bg-green-200'
                            }`}
                          >
                            {post.is_published ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            onClick={() => handleEditPost(post)}
                            className="mx-1 px-3 py-1 bg-blue-100 text-blue-600 rounded text-xs font-medium hover:bg-blue-200 transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeletePost(post.id)}
                            className="mx-1 px-3 py-1 bg-red-100 text-red-600 rounded text-xs font-medium hover:bg-red-200 transition"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>
          )}

          {/* Create View - AI Blog Generation */}
          {activeView === 'create' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Input Form */}
              <div className="space-y-6">
                {/* Blog Type Selection */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Blog Type</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {blogTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setAiFormData(prev => ({ ...prev, blogType: type.id }))}
                        className={`p-4 rounded-lg border-2 transition text-left ${
                          aiFormData.blogType === type.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="text-2xl">{type.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">{type.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Mode Selection */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Content Mode</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {contentModeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setAiFormData(prev => ({ ...prev, contentMode: option.value }))}
                        className={`p-4 rounded-lg border-2 transition text-left ${
                          aiFormData.contentMode === option.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="text-2xl">{option.icon}</div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{option.label}</div>
                            <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Basic Information */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Blog Topic / Title *
                      </label>
                      <input
                        type="text"
                        name="topic"
                        value={aiFormData.topic}
                        onChange={handleAiInputChange}
                        placeholder="e.g., How to Build a Successful Email Marketing Campaign"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Audience *
                      </label>
                      <input
                        type="text"
                        name="targetAudience"
                        value={aiFormData.targetAudience}
                        onChange={handleAiInputChange}
                        placeholder="e.g., Small business owners, Digital marketers, Beginners"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Main Points to Cover
                      </label>
                      <textarea
                        name="mainPoints"
                        value={aiFormData.mainPoints}
                        onChange={handleAiInputChange}
                        placeholder="List the key points or sections you want to include (one per line)"
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Keywords (comma separated)
                      </label>
                      <input
                        type="text"
                        name="keywords"
                        value={aiFormData.keywords}
                        onChange={handleAiInputChange}
                        placeholder="e.g., email marketing, campaign strategy, conversion rate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Writing Preferences */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Writing Preferences</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type (Category)</label>
                      <select
                        name="category"
                        value={aiFormData.category || ''}
                        onChange={handleAiInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select a category</option>
                        <option value="Technology">Technology</option>
                        <option value="AI & Machine Learning">AI & Machine Learning</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Business">Business</option>
                        <option value="Design">Design</option>
                        <option value="Development">Development</option>
                        <option value="Data Science">Data Science</option>
                        <option value="Product">Product</option>
                        <option value="Industry News">Industry News</option>
                        <option value="Tutorials">Tutorials</option>
                        <option value="Case Studies">Case Studies</option>
                        <option value="Best Practices">Best Practices</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Choose the blog category for organization</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Word Count</label>
                      <select
                        name="wordCount"
                        value={aiFormData.wordCount}
                        onChange={handleAiInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {wordCountOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
                      <select
                        name="tone"
                        value={aiFormData.tone}
                        onChange={handleAiInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {toneOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label} - {option.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Writing Style</label>
                      <select
                        name="writingStyle"
                        value={aiFormData.writingStyle}
                        onChange={handleAiInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {writingStyleOptions.map(style => (
                          <option key={style.value} value={style.value}>{style.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                      <select
                        name="language"
                        value={aiFormData.language}
                        onChange={handleAiInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {languageOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.flag} {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Generation Status */}
                {isGenerating && generationStatus && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <svg className="animate-spin h-5 w-5 text-blue-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">{generationStatus}</p>
                        <p className="text-xs text-blue-600 mt-1">This may take 30-60 seconds...</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generate Button */}
                <button
                  onClick={handleGenerateBlog}
                  disabled={isGenerating}
                  className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition ${
                    isGenerating
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isGenerating ? 'Generating Blog Post...' : 'Generate Blog Post'}
                </button>
              </div>

              {/* Right Column - Generated Content */}
              <div className="space-y-6">
                {generatedContent ? (
                  <>
                    {/* Preview */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">Generated Blog Preview</h2>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSaveAsDraft}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                          >
                            {loading ? 'Saving...' : 'Save as Draft'}
                          </button>
                          <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{generatedContent.title}</h3>
                        </div>

                        {generatedContent.metaDescription && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm text-gray-700">{generatedContent.metaDescription}</p>
                          </div>
                        )}

                        <div className="prose max-w-none">
                          <ReactMarkdown>{generatedContent.fullContent}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <div className="text-gray-400 mb-4">
                      <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Blog Content Yet</h3>
                    <p className="text-gray-500">Fill in the form and click "Generate Blog Post" to create your content</p>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Edit Modal - Enhanced Version */}
        {showEditModal && editingPost && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Edit Blog Post</h2>
                    <p className="text-sm text-gray-500 mt-1">Make changes to your blog post content and metadata</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingPost(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Form Fields */}
                  <div className="space-y-6">
                    {/* Basic Information Card */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter blog title"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Excerpt
                          </label>
                          <textarea
                            name="excerpt"
                            value={formData.excerpt}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Brief summary of the blog post"
                          />
                          <p className="text-xs text-gray-500 mt-1">This will be displayed in blog listings</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Author Name</label>
                            <input
                              type="text"
                              name="author_name"
                              value={formData.author_name}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Author name"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                            <select
                              name="category"
                              value={formData.category}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select a category</option>
                              <option value="Technology">Technology</option>
                              <option value="AI & Machine Learning">AI & Machine Learning</option>
                              <option value="Marketing">Marketing</option>
                              <option value="Business">Business</option>
                              <option value="Design">Design</option>
                              <option value="Development">Development</option>
                              <option value="Data Science">Data Science</option>
                              <option value="Product">Product</option>
                              <option value="Industry News">Industry News</option>
                              <option value="Tutorials">Tutorials</option>
                              <option value="Case Studies">Case Studies</option>
                              <option value="Best Practices">Best Practices</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                          <input
                            type="text"
                            value={formData.tags.join(', ')}
                            onChange={handleTagsChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="ai, technology, marketing (comma-separated)"
                          />
                        </div>

                        <div>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              name="is_published"
                              checked={formData.is_published}
                              onChange={handleInputChange}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Publish this post</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-1 ml-6">
                            {formData.is_published ? 'This post will be visible to the public' : 'This post will be saved as a draft'}
                          </p>
                        </div>

                        {/* Cover Image integrated into Basic Information */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image URL</label>
                          <input
                            type="text"
                            name="cover_image_url"
                            value={formData.cover_image_url}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="https://example.com/image.jpg"
                          />
                          <p className="text-xs text-gray-500 mt-1">Enter the URL of your cover image</p>
                        </div>

                        {/* Image Preview */}
                        {formData.cover_image_url && (
                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <img
                                src={formData.cover_image_url}
                                alt="Cover preview"
                                className="w-full h-48 object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Content Editor */}
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Content</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Blog Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="content"
                          value={formData.content}
                          onChange={handleInputChange}
                          rows={20}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder="Write your blog content here... (Markdown supported)"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Supports Markdown: Use # for headings, **bold**, *italic*, [links](url), ![images](url)
                        </p>
                      </div>
                    </div>

                    {/* Content Preview */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
                      <div className="prose max-w-none max-h-64 overflow-y-auto">
                        <ReactMarkdown>{formData.content || '*No content yet...*'}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {editingPost.updated_at && (
                      <span>Last updated: {new Date(editingPost.updated_at).toLocaleString()}</span>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingPost(null);
                      }}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdatePost}
                      disabled={loading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export default Blog;
